import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Sparkles, Send, Loader2, RefreshCw, Plug, ChevronRight, Clock, Zap } from "lucide-react";

/**
 * Ask ForensiQ — natural-language SecOps assistant.
 * User types a question → Claude plans multi-tool queries → connectors fan out
 * live → Claude synthesizes a markdown report. Nothing persists beyond a
 * 15-minute cache.
 */
export default function AskForensiQ() {
  const [question, setQuestion] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [tools, setTools] = useState([]);
  const [history, setHistory] = useState([]);   // in-memory only, per session
  const bottomRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const [s, c] = await Promise.all([
          api.get("/ai/suggestions"),
          api.get("/ai/catalog"),
        ]);
        setSuggestions(s.data.suggestions || []);
        setTools(c.data.tools || []);
      } catch (e) { /* silent */ }
    })();
  }, []);

  const ask = async (q) => {
    const question_ = (q ?? question).trim();
    if (!question_) return;
    setBusy(true);
    setResult(null);
    try {
      const r = await api.post("/ai/ask", { question: question_ });
      setResult(r.data);
      setHistory((h) => [{ question: question_, at: new Date().toISOString(),
                           cache: r.data.cache }, ...h].slice(0, 8));
      setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: "smooth" }), 200);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Query failed");
    } finally {
      setBusy(false);
    }
  };

  const runSuggestion = (s) => {
    setQuestion(s);
    ask(s);
  };

  return (
    <div className="p-8 max-w-[1600px] mx-auto" data-testid="ask-forensiq-page">
      <header className="mb-6">
        <div className="text-[11px] font-mono uppercase tracking-widest text-[#8E9196] flex items-center gap-2">
          <Sparkles className="w-3.5 h-3.5 text-[#B57BFF]" /> AI SecOps assistant · live tool orchestration
        </div>
        <h1 className="font-display font-black text-5xl mt-2">Ask ForensiQ</h1>
        <p className="text-[#8E9196] mt-1 text-sm">
          One question. All your tools. Claude plans multi-tool queries, connectors fan out live,
          and you get an executive-ready answer — no log storage, no SIEM.
        </p>
      </header>

      {/* Search bar */}
      <div className="border border-white/15 bg-[#0a0a0a] rounded-sm p-4 mb-4" data-testid="ask-form">
        <div className="flex items-center gap-2">
          <Sparkles className="w-5 h-5 text-[#B57BFF] shrink-0" />
          <input
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && !busy && ask()}
            placeholder="e.g. Any infection past 24 hours? Any bad IP allowed by firewall?"
            data-testid="ask-input"
            className="flex-1 bg-transparent outline-none text-lg font-mono placeholder:text-[#52525B]"
            autoFocus
          />
          <button onClick={() => ask()} disabled={busy || !question.trim()}
            data-testid="ask-submit"
            className="inline-flex items-center gap-2 border border-[#B57BFF] text-[#B57BFF] hover:bg-[#B57BFF] hover:text-black px-3 py-2 rounded-sm font-mono uppercase text-xs tracking-widest disabled:opacity-40">
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />} Ask
          </button>
        </div>
        {/* Suggestions */}
        <div className="mt-3 flex flex-wrap gap-1.5">
          {suggestions.map((s) => (
            <button key={s} onClick={() => runSuggestion(s)} data-testid={`suggest-${s.slice(0, 10)}`}
              className="text-[11px] font-mono text-[#8E9196] hover:text-[#B57BFF] border border-white/10 hover:border-[#B57BFF]/60 px-2 py-1 rounded-sm">
              {s}
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[300px_1fr] gap-4">
        {/* Sidebar — tools + history */}
        <aside className="space-y-4">
          <div className="border border-white/10 bg-[#121212] rounded-sm p-4">
            <div className="text-[10px] font-mono uppercase tracking-widest text-[#8E9196] mb-2 flex items-center gap-1.5">
              <Plug className="w-3 h-3" /> Connected tools
            </div>
            {tools.length === 0 && (
              <div className="text-xs text-[#8E9196]">No queryable connectors yet. <a className="text-[#00F0FF] hover:underline" href="/admin/connectors">Add one</a>.</div>
            )}
            {tools.map((t) => (
              <div key={t.type} className="mb-3 last:mb-0" data-testid={`tool-${t.type}`}>
                <div className="text-sm">{t.label}</div>
                <div className="text-[9px] font-mono uppercase tracking-widest text-[#52525B]">{t.queries.join(" · ")}</div>
              </div>
            ))}
          </div>
          {history.length > 0 && (
            <div className="border border-white/10 bg-[#121212] rounded-sm p-4">
              <div className="text-[10px] font-mono uppercase tracking-widest text-[#8E9196] mb-2 flex items-center gap-1.5">
                <Clock className="w-3 h-3" /> Recent questions
              </div>
              <ol className="space-y-1.5">
                {history.map((h, i) => (
                  <li key={i}>
                    <button onClick={() => runSuggestion(h.question)}
                      className="text-left w-full text-xs text-white/80 hover:text-[#B57BFF] flex items-start gap-1">
                      <ChevronRight className="w-3 h-3 mt-0.5 shrink-0" />
                      <span className="flex-1">
                        {h.question}
                        {h.cache?.hit && <span className="ml-1 text-[9px] font-mono text-[#00F0FF]">CACHED</span>}
                      </span>
                    </button>
                  </li>
                ))}
              </ol>
            </div>
          )}
        </aside>

        {/* Main result */}
        <main>
          {busy && (
            <div className="border border-[#B57BFF]/40 bg-[#B57BFF]/5 rounded-sm p-6 flex items-center gap-3">
              <Loader2 className="w-5 h-5 animate-spin text-[#B57BFF]" />
              <div>
                <div className="text-sm">Planning multi-tool query…</div>
                <div className="text-xs text-[#8E9196]">Claude is deciding which connectors to hit and fanning out live.</div>
              </div>
            </div>
          )}

          {!busy && !result && (
            <div className="border border-dashed border-white/10 rounded-sm p-10 text-center">
              <Sparkles className="w-8 h-8 text-[#B57BFF] mx-auto mb-3" />
              <div className="font-display text-xl">Ask any security question</div>
              <div className="text-[#8E9196] text-sm mt-1">
                Try one of the suggestions above, or type your own.
              </div>
            </div>
          )}

          {!busy && result && (
            <div className="space-y-4" data-testid="ask-result">
              {/* Plan */}
              <div className="border border-white/10 bg-[#0f0f10] rounded-sm p-4">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-[10px] font-mono uppercase tracking-widest text-[#8E9196]">Query plan</div>
                  {result.cache?.hit ? (
                    <span className="text-[9px] font-mono uppercase text-[#00F0FF]">Cached · {new Date(result.cache.cached_at).toLocaleTimeString()}</span>
                  ) : (
                    <span className="text-[9px] font-mono uppercase text-[#B57BFF] flex items-center gap-1">
                      <Zap className="w-3 h-3" /> Live · {new Date(result.executed_at).toLocaleTimeString()}
                    </span>
                  )}
                </div>
                <div className="text-sm text-white/80 mb-3 italic">{result.plan?.rationale}</div>
                <div className="space-y-2">
                  {(result.steps || []).map((s, i) => (
                    <div key={i} className="flex items-start gap-2 text-xs font-mono">
                      <span className="text-[#B57BFF]">[{String(i + 1).padStart(2, "0")}]</span>
                      <div className="flex-1">
                        <div>
                          <span className="text-[#00F0FF]">{s.connector_type}</span>.
                          <span className="text-white">{s.query}</span>
                          <span className="text-[#8E9196]">({JSON.stringify(s.params || {})})</span>
                          {s.ok ? (
                            <span className="ml-2 text-[#34C759]">→ {s.count} result{s.count !== 1 ? "s" : ""}</span>
                          ) : (
                            <span className="ml-2 text-[#FF3B30]">→ {s.detail}</span>
                          )}
                        </div>
                        {s.reason && <div className="text-[#8E9196] font-sans mt-0.5">{s.reason}</div>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Report */}
              <div className="border border-white/10 bg-[#121212] rounded-sm p-6" data-testid="ask-report">
                <div className="text-[10px] font-mono uppercase tracking-widest text-[#8E9196] mb-3">Report</div>
                <article className="prose prose-invert prose-sm max-w-none prose-headings:font-display prose-headings:font-black prose-a:text-[#00F0FF] prose-strong:text-white prose-code:text-[#B57BFF] prose-code:bg-white/5 prose-code:px-1 prose-code:rounded prose-code:before:content-[''] prose-code:after:content-['']">
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>{result.report_md || "_No report generated._"}</ReactMarkdown>
                </article>
              </div>

              {/* Raw expandable */}
              <details className="border border-white/10 bg-[#0a0a0a] rounded-sm">
                <summary className="p-3 cursor-pointer text-[10px] font-mono uppercase tracking-widest text-[#8E9196]">
                  Raw connector output
                </summary>
                <pre className="p-3 text-[10px] overflow-x-auto text-white/70">{JSON.stringify(result.steps, null, 2)}</pre>
              </details>

              <div ref={bottomRef} />
            </div>
          )}
        </main>
      </div>
    </div>
  );
}
